import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import apiInstance from '../utils/axios';

const CategoriesGrid = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const MAX_VISIBLE = 10; // 5 top row + 5 bottom row

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiInstance.get('category/');
        const categoriesData = Array.isArray(response.data) 
          ? response.data 
          : (response.data.results || []);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleCategoryClick = (categoryId) => {
    navigate(`/products?category=${categoryId}`);
  };

  if (loading) {
    return (
      <section className="py-6 md:py-8">
        <div className="mx-auto max-w-7xl px-4">
          <div className="h-6 w-40 bg-secondary rounded animate-pulse mb-4" />
          {/* 5 per row skeleton */}
          <div className="grid grid-cols-5 gap-1 md:gap-2">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="w-[100px] h-[100px] md:w-[144px] md:h-[144px] rounded-full bg-secondary animate-pulse mb-1" />
                <div className="h-3 w-12 bg-secondary rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!categories.length) return null;

  const visibleCategories = categories.slice(0, MAX_VISIBLE);
  const hasMore = categories.length > MAX_VISIBLE;

  return (
    <section className="py-6 md:py-8">
      <div className="mx-auto max-w-7xl px-4">
        <h2 className="text-lg md:text-xl font-bold text-foreground mb-3 md:mb-4">
          {t('allCategories')}
        </h2>
        {/* 5 categories per row */}
        <div className="grid grid-cols-5 gap-1 md:gap-2">
          {visibleCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => handleCategoryClick(category.id)}
              className="group flex flex-col items-center text-center"
            >
              {/* 30% bigger circular icons */}
              <div className="relative w-[100px] h-[100px] md:w-[144px] md:h-[144px] rounded-full overflow-hidden bg-white mb-1.5 border border-border/50 group-hover:border-primary group-hover:shadow-lg transition-all duration-200">
                <img
                  src={category.image || '/placeholder.jpg'}
                  alt={category.title_translated || category.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <span className="text-[11px] md:text-sm font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2">
                {category.title_translated || category.title}
              </span>
            </button>
          ))}
        </div>
        
        {/* Show all categories button */}
        {hasMore && (
          <div className="flex justify-center mt-6">
            <Link
              to="/categories"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary/10 hover:bg-primary/20 text-primary font-medium rounded-full transition-colors"
            >
              {t('showAllCategories') || 'Ver todas as categorias'}
              <span className="bg-primary/20 text-primary text-xs px-2 py-0.5 rounded-full">
                +{categories.length - MAX_VISIBLE}
              </span>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default CategoriesGrid;
